import React, { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import GitHubIcon from "@mui/icons-material/GitHub";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, CircularProgress, MenuItem, TextField, Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useUpdateUserMembershipBulkMutation, useUpdateUserOrgStatusMutation } from "state/rtkQuery/user/userMutations";
import {
  useGetCostCenterUsersQuery,
  useLazyGetUserQuery,
  useGetUsersByOrgAndStatusQuery,
} from "state/rtkQuery/user/userQueries";
import {
  EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW,
  EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW,
} from "utils/constants";
import { useDebounce } from "utils/useDebounce";

import dayjs from "dayjs";

import ChangeCostCenterModal from "./ChangeCostCenterModal";
import successGIF from "./cost-center-success.gif";

import AvatarRandomColor from "../ReusableDataGrid/AvatarRandomColor";
import ReusableActions from "../ReusableDataGrid/ReusableActions";
import ReusableDataGrid from "../ReusableDataGrid/ReusableDataGrid";
import UserDetailsModal from "../../../admin/components/UserDetailsModal/UserDetailsModal";
import UserDetailModal from "../../../admin/components/CopilotDashboard/UserDetailModal";
import { getPresetRange } from "../../../admin/components/CopilotDashboard/datePresets";
import { useTabSortFilter } from "../hooks/useTabSortFilter";

// The Copilot usage popup opened from the GitHub icon always shows the
// current month — same range "This Month" resolves to on the Copilot
// Dashboard. To see other date ranges, admins go to that dashboard directly.
// Computed fresh on each open rather than once at module load, so a
// long-lived session doesn't get stuck showing a stale month.
const getCurrentMonthRange = () => {
  const { from, to } = getPresetRange("this_month");
  return { from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") };
};

const JoinedTab = ({ orgName }) => {
  const [columns, setColumns] = useState([]);
  const [updatedRows, setUpdatedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const { orgId } = useParams();
  const [orgPermissions, setOrgPermissions] = useState();
  const [usersCostCenters, setUsersCostCenters] = useState(null);
  const [emailTruncateLimit, setEmailTruncateLimit] = useState(
    EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW
  );
  const [selectedUser, setSelectedUser] = useState(null);
  const [newCostCenter, setNewCostCenter] = useState({ id: "", name: "" });
  const [oldCostCenter, setOldCostCenter] = useState({ id: "", name: "" });
  const [open, setOpen] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");
  const [selectedCopilotUser, setSelectedCopilotUser] = useState(null);
  const permissions = useSelector((state) => state.auth.permissions);
  const [triggerGetUser] = useLazyGetUserQuery();

  useEffect(() => {
    if (orgId && permissions) {
      setOrgPermissions(
        permissions?.filter((permission) => permission.orgId === orgId)
      );
    }
  }, [orgId, permissions]);

  useEffect(() => {
    if (orgPermissions && orgPermissions?.[0]?.userType === "Approver") {
      setEmailTruncateLimit(
        EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW
      );
    }
  }, [orgPermissions]);

  const {
    data: platformData,
    isFetching: isPlatformDataLoading,
    isError: isPlatformDataError,
  } = useGetPlatformsQuery();

  // RTK Query to get Cost Center List

  const {
    data: costCenterListData,
    isFetching: isCostCenterListDataLoading,
    isSuccess: isCostCenterListDataSuccess,
    isError: isCostCenterListDataError,
  } = useGetCostCenterUsersQuery(
    {
      platformId: platformData?._id,
    },
    { skip: !platformData?._id }
  );

  const { data: allOrgs, isSuccess: isOrgsSuccess } =
    useGetActiveOrganizationsQuery();

  // Filter options for this tab
  const filterOptions = useMemo(() => {
    const options = [
      { value: "first_name", label: "First Name" },
      { value: "last_name", label: "Last Name" },
      { value: "changed_date", label: "Date of Joining" },
      { value: "changed_by", label: "Approved By" },
    ];

    if (orgPermissions?.[0]?.userType === "Owner") {
      options.push({ value: "cost_center_id", label: "Cost Center" });
    }

    return options;
  }, [orgPermissions]);

  // Dropdown options for specific fields (like cost center)
  const dropdownOptions = useMemo(() => {
    const options = {};
    
    if (orgPermissions?.[0]?.userType === "Owner" && costCenterListData?.cost_centers) {
      options.cost_center_id = costCenterListData.cost_centers;
    }
    
    return options;
  }, [orgPermissions, costCenterListData]);

  // Use the sorting and filtering hook
  const {
    filterField,
    sortBy,
    sortOrder,
    formattedFilterValue,
    nameSortMenuAnchor,
    debouncedEmailSearch,
    handleSortClick,
    handleDirectSort,
    handleNameSortMenuOpen,
    renderNameSortMenu,
    renderNameSortIcon,
    renderSortIcon,
    renderFilterFieldSelect,
    renderDynamicSearchInput,
  } = useTabSortFilter(filterOptions, dropdownOptions, setCurrentPage);

  //RTK Query to get users by org and status
  const {
    data: joinedUserListData,
    isFetching: isJoinedUserListDataLoading,
    isSuccess: isJoinedUserListDataSuccess,
    isError: isJoinedUserListDataError,
  } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Joined",
      platformId: platformData?._id,
      page: currentPage,
      perPage: 25,
      email: debouncedEmailSearch === "" ? undefined : debouncedEmailSearch,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
      filterField: filterField || undefined,
      filterValue: formattedFilterValue === "" ? undefined : formattedFilterValue,
    },
    { skip: !platformData?._id, refetchOnMountOrArgChange: true }
  );

  // Set datagrid rows
  // Combines both joined and remove requested users into a single list
  useEffect(() => {
    if (isJoinedUserListDataSuccess && joinedUserListData?.users) {
      // User cost centers need to be set in a separate state to allow for dynamic updates on the table by org owners and approvers
      joinedUserListData?.users?.map((user) => {
        setUsersCostCenters((prev) => ({
          ...prev,
          [user.id]: user?.costCenter?.length > 0 ? user.costCenter : "",
        }));
      });
      setUpdatedRows(joinedUserListData?.users);
    }
  }, [joinedUserListData, isJoinedUserListDataSuccess]);

  // Bulk mutation
  const [
    updateUserMembershipBulk,
    { isLoading: isUpdateUserMembershipBulkLoading },
  ] = useUpdateUserMembershipBulkMutation();

  const userEmail = useSelector((state) => state.auth.userAccount?.username);

  // Bulk handler
  const handleBulkAction = async (userIds, rejectReason, otherReason) => {
    try {
      const bulkPayload = {
        platforms: {
          [platformData?._id]: {
            organizations: {
              [orgId]: {
                membership_status: "Remove-Requested",
                reason: rejectReason === "Other" ? otherReason : rejectReason,
                changed_by: userEmail?.toLowerCase(),
              },
            },
          },
        },
        user_ids: userIds,
      };
      await updateUserMembershipBulk({
        body: bulkPayload,
        invalidateStatuses: ["Joined", "Remove-Requested"],
      });
    } catch (error) {
      // Handle error
    }
  };

  // Single user action with status check
  // Single user action with status check - use single-user mutation when acting on one user
  const [updateUserOrgStatus] = useUpdateUserOrgStatusMutation();

  const singleUserAction = (params) => async (rejectReason, otherReason) => {
    if (params.row.status === "joined") {
      try {
        const singlePayload = {
          platforms: {
            [platformData?._id]: {
              organizations: {
                [orgId]: {
                  membership_status: "Remove-Requested",
                  reason: rejectReason === "Other" ? otherReason : rejectReason,
                  changed_by: userEmail?.toLowerCase(),
                },
              },
            },
          },
        };

        await updateUserOrgStatus({
          userId: params.row.id,
          body: singlePayload,
          attribute: "membership_status",
        }).unwrap();
      } catch (err) {
        // fallback to bulk handler
        handleBulkAction([params.row.id], rejectReason, otherReason);
      }
    }
  };

  // Multi user action (no status check, as you should filter selectedRows before calling)
  const multiUserAction = (userIds, rejectReason, otherReason) => {
    handleBulkAction(userIds, rejectReason, otherReason);
  };

  const orgIdToName = useMemo(() => {
    if (!isOrgsSuccess || !Array.isArray(allOrgs)) {
      return {};
    }

    return allOrgs.reduce((acc, org) => {
      acc[org._id] = org.name;
      return acc;
    }, {});
  }, [allOrgs, isOrgsSuccess]);

  const costCenterById = useMemo(() => {
    const costCenters = costCenterListData?.cost_centers;

    if (!isCostCenterListDataSuccess || !Array.isArray(costCenters)) {
      return {};
    }

    return costCenters.reduce((acc, costCenter) => {
      acc[costCenter._id] = costCenter.name;
      return acc;
    }, {});
  }, [costCenterListData, isCostCenterListDataSuccess]);

  const handleOpenUserDetails = async (row) => {
    setShowUserDetailsModal(true);
    setIsUserDetailsLoading(true);
    setUserDetailsError("");

    try {
      const result = await triggerGetUser({
        email: row.email,
        platformId: platformData?._id,
      });
      const users = Array.isArray(result?.data) ? result.data : [];
      const exactMatch = users.find((user) => user._id === row.id);

      setSelectedUserDetails(exactMatch || users[0] || null);

      if (!users.length) {
        setUserDetailsError("Could not find user details for this user.");
      }
    } catch (error) {
      setSelectedUserDetails(null);
      setUserDetailsError("Failed to load user details. Please try again.");
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  // Function to execute when cost center dropdown is changed
  const updateCostCenter = (userId, platformId, costCenterID) => {
    const loadingToast = toast.loading("Updating cost center for user", {
      style: {
        minWidth: "300px",
      },
    });

    updateUserOrgStatus({
      userId: userId,
      body: {
        platforms: {
          [platformId]: {
            cost_center_id: costCenterID,
          },
        },
      },
      attribute: "cost_center_id",
    })
      .unwrap()
      .then(() => {
        toast.dismiss(loadingToast);
        toast(
          <div>
            <img
              src={successGIF}
              height={"40px"}
              width={"40px"}
              alt="Success Icon"
            />
            Cost center updated successfully!
          </div>,
          {
            duration: 3500,
            style: {
              minWidth: "fit-content",
            },
          }
        );
      })
      .catch((error) => {
        toast.dismiss(loadingToast);
        // toast.error(`Failed to update cost center: ${error.toString()}`, {
        toast.error(`Failed to update cost center. Please try again later.`, {
          duration: 6000,
          style: {
            minWidth: "fit-content",
          },
        });
        console.error("Failed to update cost center:", error);
      });
  };

  // Handle cost center dropdown on-change evebt
  const handleCostCenterChange = (user, newCostCenterName) => {
    const newCostCenter = costCenterListData?.cost_centers?.find(
      (costCenter) => costCenter.name === newCostCenterName
    );

    const oldCostCenterId = usersCostCenters[user.id] || "";
    const oldCostCenter = costCenterListData?.cost_centers?.find(
      (costCenter) => costCenter._id === oldCostCenterId
    );

    const oldCostCenterName = oldCostCenter
      ? oldCostCenter.name
      : "Not assigned";

    setSelectedUser(user);
    setNewCostCenter({
      id: newCostCenter?._id || "",
      name: newCostCenterName,
    });
    setOldCostCenter({
      id: oldCostCenterId,
      name: oldCostCenterName,
    });

    setOpen(true);
  };

  const handleCostCenterModalConfirmationChange = () => {
    setUsersCostCenters((prev) => ({
      ...prev,
      [selectedUser?.id]: newCostCenter,
    }));
    setOpen(false);
    updateCostCenter(selectedUser.id, platformData?._id, newCostCenter.id)();
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Set columns
  useEffect(() => {
    if (updatedRows && orgPermissions) {
      setColumns([
        {
          field: "profile",
          headerName: "Profile",
          headerAlign: "center",
          align: "center",
          flex: 0.4,
          minWidth: 55,
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <Tooltip title="View user details">
              <IconButton
                size="small"
                aria-label="View user details"
                onClick={() => handleOpenUserDetails(params.row)}
                sx={{ p: 0.25 }}
              >
                <InfoOutlinedIcon sx={{ color: "#0084ff", fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          ),
        },
        {
          field: "copilot",
          headerName: "Copilot",
          headerAlign: "center",
          align: "center",
          flex: 0.5,
          minWidth: 70,
          sortable: false,
          filterable: false,
          renderCell: (params) =>
            params.row.copilotBudget?.licensed_organizations?.length > 0 ? (
              <Tooltip
                title={
                  <span style={{ fontSize: "12px" }}>
                    View this month's Copilot usage
                    <br />
                    Has a Copilot license in:{" "}
                    {params.row.copilotBudget.licensed_organizations
                      .map((id) => orgIdToName[id] || id)
                      .join(", ")}
                  </span>
                }
                arrow
              >
                <IconButton
                  size="small"
                  aria-label="View Copilot usage"
                  onClick={() => setSelectedCopilotUser(params.row)}
                  sx={{ p: 0.25, color: "#1a1a1a" }}
                >
                  <GitHubIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ) : null,
        },
        {
          field: "name",
          headerName: `Showing ${(currentPage - 1) * 25 + 1} - ${(currentPage - 1) * 25 + updatedRows?.length
            } of ${joinedUserListData?.total || 0} Users`,
          headerClassName:
            orgPermissions?.[0]?.userType === "Owner"
              ? "history-tab-totalCount-header"
              : "history-tab-totalCount-header-approver-view",
          headerAlign: "left",
          align: "left",
          flex: 2,
          minWidth: orgPermissions?.[0]?.userType === "Owner" ? 270 : 350,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className={orgPermissions?.[0]?.userType === "Owner" ? "history-tab-totalCount-header" : "history-tab-totalCount-header-approver-view"}
                style={{ 
                  overflow: "hidden", 
                  textOverflow: "ellipsis", 
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0
                }}
              >
                {`Showing ${(currentPage - 1) * 25 + 1} - ${(currentPage - 1) * 25 + updatedRows?.length} of ${joinedUserListData?.total || 0} Users`}
              </span>
              {renderNameSortIcon()}
              {renderNameSortMenu(setCurrentPage)}
            </Box>
          ),
          renderCell: (params) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                paddingLeft:
                  orgPermissions?.[0]?.userType === "Owner" ? "0px" : "30px",
              }}
            >
              <AvatarRandomColor
                firstName={params.row.firstName}
                lastName={params.row.lastName}
                color={params.row.color}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "20px",
                }}
              >
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.name}</span>
                  }
                  placement="top-start"
                  arrow
                >
                  <span
                    className="table-history-userEmailJoined-cell"
                    style={{ fontWeight: "600", fontSize: "14px" }}
                  >
                    {params.row.name}
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.email}</span>
                  }
                  arrow
                  placement="bottom-start"
                >
                  {params.row.email.length > emailTruncateLimit ? (
                    <div>
                      <span
                        className="table-cell-trucate-reason"
                        style={{ paddingLeft: "0", fontSize: "13px" }}
                      >
                        {params.row.email?.slice(0, emailTruncateLimit) + "..."}
                      </span>{" "}
                    </div>
                  ) : (
                    <span style={{ fontSize: "13px" }}>{params.row.email}</span>
                  )}
                </Tooltip>
              </div>
            </div>
          ),
        },
        {
          field: "JoinedDate",
          headerName: "Date of Joining",
          headerClassName: "date-joined-table-header",
          headerAlign: "center",
          align: "center",
          flex: 1,
          minWidth: orgPermissions?.[0]?.userType === "Owner" ? 100 : 240,
          valueGetter: (params) => {
            // Return the date as a Date object for proper sorting
            return new Date(params.row.date);
          },
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="date-joined-table-header date-joined-title"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Date of Joining
              </span>
              {renderSortIcon("changed_date", "Date of Joining")}
            </Box>
          ),
          renderCell: (params) => (
            <span className="history-tab-joinedDate-cell ">
              {params.row.date}
            </span>
          ),
        },
        {
          field: "approvedBy",
          headerName: "Approved By",
          headerClassName: "orgName-column-header",
          headerAlign: "center",
          align: "center",
          flex: 1.5,
          minWidth: orgPermissions?.[0]?.userType === "Owner" ? 200 : 350,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="orgName-column-header approved-by-title"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Approved By
              </span>
              {renderSortIcon("changed_by", "Approved By")}
            </Box>
          ),
          renderCell: (params) => (
            <Tooltip
              title={<p style={{ fontSize: "12px" }}>{params.row.changedBy}</p>}
            >
              <span className="table-cell-history">{params.row.changedBy}</span>
            </Tooltip>
          ),
        },
        //Cost Center field
        orgPermissions?.[0]?.userType === "Owner" && {
          field: "costCenter",
          headerName: "Cost Center",
          headerAlign: "center",
          align: "center",
          backgroundColor: "red",
          flex: 1.3,
          minWidth: 180,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span
                className="cost-center-title"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Cost Center
              </span>
              {renderSortIcon("cost_center", "Cost Center")}
            </Box>
          ),
          renderCell: (params) => {
            if (
              !usersCostCenters ||
              isCostCenterListDataLoading ||
              !isCostCenterListDataSuccess
            ) {
              return <CircularProgress size={24} />;
            }
            const userCostCenterId = usersCostCenters[params.row.id] || "";
            const costCenter = costCenterListData?.cost_centers?.find(
              (option) => option._id === userCostCenterId
            );
            const userCostCenterName = costCenter
              ? costCenter.name
              : "Select option";

            return (
              <Tooltip title={userCostCenterName} placement="top">
                <TextField
                  sx={{
                    "& .MuiInputBase-input": { padding: "7.5px 14px" },
                  }}
                  id="costCenter"
                  select
                  value={userCostCenterName}
                  onChange={(e) =>
                    handleCostCenterChange(params.row, e.target.value)
                  }
                  variant="outlined"
                  style={{ width: "100%" }}
                  defaultValue=""
                >
                  {userCostCenterName === "Select option" && (
                    <MenuItem value="Select option" disabled>
                      Select option
                    </MenuItem>
                  )}
                  {costCenterListData?.cost_centers?.map((option) => (
                    <MenuItem key={option?._id} value={option?.name}>
                      {option?.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Tooltip>
            );
          },
        },
        orgPermissions?.[0]?.userType === "Owner" && {
          field: "action",
          headerAlign: "center",
          headerName: "Actions",
          align: "center",
          flex: 1,
          minWidth: orgPermissions?.[0]?.userType === "Owner" ? 130 : 0,
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <ReusableActions
              params={params}
              action={singleUserAction(params)}
            />
          ),
        },
      ].filter(Boolean));
    }
  }, [
    updatedRows,
    orgPermissions,
    usersCostCenters,
    costCenterListData,
    isCostCenterListDataSuccess,
    currentPage,
    joinedUserListData?.total,
    emailTruncateLimit,
    platformData?._id,
    orgIdToName,
    sortBy,
    sortOrder,
    nameSortMenuAnchor,
    handleDirectSort,
  ]);

  const currentMonthRange = getCurrentMonthRange();

  return (
    <div>
      <UserDetailModal
        open={Boolean(selectedCopilotUser)}
        onClose={() => setSelectedCopilotUser(null)}
        user={selectedCopilotUser}
        orgId={orgId}
        platformId={platformData?._id}
        fromDate={currentMonthRange.from}
        toDate={currentMonthRange.to}
        footerNote="Showing current month usage only. Visit the Copilot Dashboard to view other date ranges."
      />
      <UserDetailsModal
        open={showUserDetailsModal}
        onClose={() => {
          setShowUserDetailsModal(false);
          setSelectedUserDetails(null);
          setUserDetailsError("");
        }}
        user={selectedUserDetails}
        platformId={platformData?._id}
        orgIdToName={orgIdToName}
        costCenterById={costCenterById}
        isLoading={isUserDetailsLoading}
        error={userDetailsError}
      />
      <ReusableDataGrid
        columns={columns}
        rows={updatedRows}
        orgName={orgName}
        viewType={"Joined History"}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        totalRecords={joinedUserListData?.total || 0}
        checkboxSelection={orgPermissions?.[0]?.userType === "Owner"}
        isLoading={
          isJoinedUserListDataLoading ||
          isUpdateUserMembershipBulkLoading ||
          isPlatformDataLoading ||
          isCostCenterListDataLoading
        }
        multiUserAction={multiUserAction}
        enableHorizontalScroll={true}
        customSearchControls={renderFilterFieldSelect}
        customSearchInput={renderDynamicSearchInput}
        tabDescription={
          "Users in Joined have been successfully added to the organization. These users will have access to the organization"
        }
      />
      <ChangeCostCenterModal
        show={open}
        close={handleClose}
        selectedUser={selectedUser}
        newCostCenter={newCostCenter}
        oldCostCenter={oldCostCenter}
        handleConfirmChange={handleCostCenterModalConfirmationChange}
      />
      <Toaster position="top-right" reverseOrder={false} />
    </div>
  );
};

export default JoinedTab;
